import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Alert,
    FormHelperText,
} from '@mui/material';
import { SmartToy as TutorIcon } from '@mui/icons-material';
import { useDataProvider, useNotify, Title, useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { RatingDisplay } from '../Ratings';
import { ratingService, RatingSummary } from '../../services/ratingService';
import { SaveToLibraryDialog, SelectFromLibraryDialog } from '../Library';
import CourseQualityDialog from './CourseQualityDialog';
import CourseEnrollmentDialog from './CourseEnrollmentDialog';
import { MaterialSymbol } from '../common/MaterialSymbol';
import { CertificateType, needsThresholdWarning } from './certificateType';
import './course-management-design.css';

interface Course {
    id: number;
    title: string;
    slug: string;
    description?: string;
    thumbnailUrl?: string;
    estimatedDuration?: number;
    difficulty: string;
    isActive: boolean;
    requireSequentialCompletion: boolean;
    certificateType?: CertificateType | null;
    lessons?: any[];
    tags?: string[];
    aiTutorEnabled: boolean;
    requireTeacherReviewForGrading?: boolean;
    enrollmentMode: string;
    recurrenceWeeks?: number | null;
    qualityScore?: number | null;
    qualityCheckData?: any;
    qualityCheckedAt?: string | null;
    isLicensedReference?: boolean;
}

interface Catalog {
    id: number;
    name: string;
}

type StatusFilter = 'aktiv' | 'inaktiv';
type SortOption = 'name' | 'dur' | 'les';

const CatalogCourses: React.FC = () => {
    const { catalogId } = useParams<{ catalogId: string }>();
    const [catalog, setCatalog] = useState<Catalog | null>(null);
    const [courses, setCourses] = useState<Course[]>([]);
    const [courseRatings, setCourseRatings] = useState<Record<number, RatingSummary>>({});
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [editingLessons, setEditingLessons] = useState<Array<{ minScore?: number | null }> | null>(null);
    const [editingCourse, setEditingCourse] = useState<Course | null>(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        description: '',
        estimatedDuration: 0,
        difficulty: 'beginner',
        isActive: true,
        requireSequentialCompletion: false,
        certificateType: '' as CertificateType | '',
        tags: '',
        aiTutorEnabled: true,
        requireTeacherReviewForGrading: false,
        enrollmentMode: 'none',
        recurrenceWeeks: ''
    });
    const [saveLibraryDialogOpen, setSaveLibraryDialogOpen] = useState(false);
    const [courseToSave, setCourseToSave] = useState<Course | null>(null);
    const [selectFromLibraryOpen, setSelectFromLibraryOpen] = useState(false);
    const [qualityDialogOpen, setQualityDialogOpen] = useState(false);
    const [qualityCourse, setQualityCourse] = useState<Course | null>(null);
    const [enrollmentDialogOpen, setEnrollmentDialogOpen] = useState(false);
    const [enrollmentCourse, setEnrollmentCourse] = useState<Course | null>(null);

    // Rein clientseitiger Anzeige-Zustand (Vorlage kursverwaltung-v2, View kvCourses)
    const [search, setSearch] = useState('');
    const [levelFilters, setLevelFilters] = useState<string[]>([]);
    const [statusFilters, setStatusFilters] = useState<StatusFilter[]>([]);
    const [sortBy, setSortBy] = useState<SortOption>('name');
    const [view, setView] = useState<'grid' | 'list'>('grid');

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const translate = useTranslate();

    const difficultyOptions = [
        { value: 'beginner', label: translate('courses.difficulty.beginner') },
        { value: 'intermediate', label: translate('courses.difficulty.intermediate') },
        { value: 'advanced', label: translate('courses.difficulty.advanced') }
    ];

    useEffect(() => {
        if (catalogId) {
            loadCatalog();
            loadCourses();
        }
    }, [catalogId]);

    const loadCatalog = async () => {
        try {
            const { data } = await dataProvider.getOne('course_catalogs', { id: parseInt(catalogId!) });
            setCatalog(data as Catalog);
        } catch (error) {
            notify(translate('courses.loadError'), { type: 'error' });
        }
    };

    const loadCourses = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('courses', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'title', order: 'ASC' },
                filter: { 'catalog.id': parseInt(catalogId!) }
            });
            setCourses(data as Course[]);

            // Load ratings for all courses
            const courseIds = (data as Course[]).map(c => c.id);
            if (courseIds.length > 0) {
                try {
                    const ratings = await ratingService.getRatingsBulk(courseIds);
                    setCourseRatings(ratings);
                } catch (error) {
                    // Ignore rating errors - ratings are optional
                }
            }
        } catch (error) {
            notify(translate('courses.loadCoursesError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const generateSlug = (title: string) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const handleOpenDialog = (course?: Course) => {
        if (course) {
            setEditingCourse(course);
            setFormData({
                title: course.title,
                slug: course.slug,
                description: course.description || '',
                estimatedDuration: course.estimatedDuration || 0,
                difficulty: course.difficulty,
                isActive: course.isActive,
                requireSequentialCompletion: course.requireSequentialCompletion,
                certificateType: course.certificateType || '',
                tags: course.tags?.join(', ') || '',
                aiTutorEnabled: course.aiTutorEnabled ?? true,
                requireTeacherReviewForGrading: course.requireTeacherReviewForGrading ?? false,
                enrollmentMode: course.enrollmentMode || 'none',
                recurrenceWeeks: course.recurrenceWeeks != null ? String(course.recurrenceWeeks) : ''
            });
        } else {
            setEditingCourse(null);
            setFormData({
                title: '',
                slug: '',
                description: '',
                estimatedDuration: 0,
                difficulty: 'beginner',
                isActive: true,
                requireSequentialCompletion: false,
                certificateType: '',
                tags: '',
                aiTutorEnabled: true,
                requireTeacherReviewForGrading: false,
                enrollmentMode: 'none',
                recurrenceWeeks: ''
            });
        }
        setSaveAttempted(false);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setSaveAttempted(false);
        setDialogOpen(false);
        setEditingCourse(null);
    };

    useEffect(() => {
        if (!dialogOpen) {
            setEditingLessons(null);
            return;
        }
        if (!editingCourse) {
            setEditingLessons([]);
            return;
        }
        setEditingLessons(null);
        let cancelled = false;
        dataProvider
            .getList('lessons', {
                pagination: { page: 1, perPage: 500 },
                sort: { field: 'sortOrder', order: 'ASC' },
                filter: { 'course.id': editingCourse.id },
            })
            .then(({ data }) => {
                if (!cancelled) setEditingLessons(data as Array<{ minScore?: number | null }>);
            })
            .catch(() => {
                if (!cancelled) setEditingLessons(null);
            });
        return () => {
            cancelled = true;
        };
    }, [dialogOpen, editingCourse, dataProvider]);

    const showThresholdWarning = editingLessons !== null && needsThresholdWarning(formData.certificateType, editingLessons);
    const certificateTypeMissing = saveAttempted && formData.certificateType === '';

    const handleSave = async () => {
        setSaveAttempted(true);
        if (formData.certificateType === '') return;
        try {
            // Parse tags from comma-separated string to array
            const tagsArray = formData.tags
                ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
                : null;

            const courseData = {
                title: formData.title,
                slug: formData.slug,
                description: formData.description,
                estimatedDuration: formData.estimatedDuration,
                difficulty: formData.difficulty,
                isActive: formData.isActive,
                requireSequentialCompletion: formData.requireSequentialCompletion,
                certificateType: formData.certificateType,
                tags: tagsArray,
                catalog: `/api/course_catalogs/${catalogId}`,
                aiTutorEnabled: formData.aiTutorEnabled,
                requireTeacherReviewForGrading: formData.requireTeacherReviewForGrading,
                enrollmentMode: formData.enrollmentMode,
                recurrenceWeeks: formData.recurrenceWeeks ? parseInt(String(formData.recurrenceWeeks)) : null
            };

            if (editingCourse) {
                await dataProvider.update('courses', {
                    id: editingCourse.id,
                    data: courseData,
                    previousData: editingCourse
                });
                notify(translate('courses.updateSuccess'), { type: 'success' });
            } else {
                await dataProvider.create('courses', {
                    data: courseData
                });
                notify(translate('courses.createSuccess'), { type: 'success' });
            }
            loadCourses();
            handleCloseDialog();
        } catch (error: any) {
            // Parse API Platform error response for better user feedback
            let errorMessage = translate('courses.saveError');

            if (error?.body) {
                // API Platform validation errors
                if (error.body['hydra:description']) {
                    errorMessage = error.body['hydra:description'];
                } else if (error.body.detail) {
                    errorMessage = error.body.detail;
                } else if (error.body.message) {
                    errorMessage = error.body.message;
                }

                // Check for unique constraint violation on slug
                if (errorMessage.includes('slug') || errorMessage.includes('Duplicate') || errorMessage.includes('unique') || errorMessage.includes('1062')) {
                    errorMessage = `Ein Kurs mit dem URL-Slug "${formData.slug}" existiert bereits. Bitte wählen Sie einen anderen Slug.`;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }

            notify(errorMessage, { type: 'error' });
            console.error('Course save error details:', error);
            // Don't close dialog - let user fix the error
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm(translate('courses.confirmDelete'))) {
            return;
        }

        try {
            await dataProvider.delete('courses', {
                id,
                previousData: {} as any
            });
            notify(translate('courses.deleteSuccess'), { type: 'success' });
            loadCourses();
        } catch (error: any) {
            const detail = error?.body?.['hydra:description'] || error?.message || '';
            notify(detail ? `${translate('courses.deleteError')}: ${detail}` : translate('courses.deleteError'), { type: 'error' });
        }
    };

    const getDifficultyLabel = (difficulty: string) => {
        const option = difficultyOptions.find(o => o.value === difficulty);
        return option ? option.label : difficulty;
    };

    const handleSaveToLibrary = (course: Course) => {
        setCourseToSave(course);
        setSaveLibraryDialogOpen(true);
    };

    const toggleFilter = <T,>(list: T[], value: T): T[] =>
        list.includes(value) ? list.filter(v => v !== value) : [...list, value];

    const resetFilters = () => {
        setLevelFilters([]);
        setStatusFilters([]);
        setSearch('');
    };

    const statusLabels: Record<StatusFilter, string> = {
        aktiv: translate('catalogs.active'),
        inaktiv: translate('catalogs.inactive')
    };

    const filterChipLabel = (value: string) =>
        value === 'aktiv' || value === 'inaktiv'
            ? statusLabels[value as StatusFilter]
            : getDifficultyLabel(value);

    const visibleCourses = useMemo(() => {
        const q = search.trim().toLowerCase();
        const result = courses.filter(course => {
            const status: StatusFilter = course.isActive ? 'aktiv' : 'inaktiv';
            if (levelFilters.length > 0 && !levelFilters.includes(course.difficulty)) return false;
            if (statusFilters.length > 0 && !statusFilters.includes(status)) return false;
            if (q && !course.title.toLowerCase().includes(q)) return false;
            return true;
        });
        result.sort((a, b) => {
            if (sortBy === 'dur') return (a.estimatedDuration || 0) - (b.estimatedDuration || 0);
            if (sortBy === 'les') return (a.lessons?.length || 0) - (b.lessons?.length || 0);
            return a.title.localeCompare(b.title, 'de');
        });
        return result;
    }, [courses, search, levelFilters, statusFilters, sortBy]);

    const activeFilterChips: string[] = [...levelFilters, ...statusFilters];

    const qualityChipClass = (score: number) =>
        score >= 75 ? 'chip-ki' : score >= 50 ? 'chip-ki warn' : 'chip-ki low';

    if (loading) {
        return (
            <div className="oa-course-mgmt">
                <Title title={`${translate('courses.title')} - ${catalog?.name || translate('courses.catalog')}`} />
                <p className="cat-empty">{translate('courses.loading')}</p>
            </div>
        );
    }

    return (
        <div className="oa-course-mgmt">
            <Title title={`${translate('courses.title')} - ${catalog?.name || translate('courses.catalog')}`} />

            <button type="button" className="back-link" onClick={() => navigate('/courses/catalogs')}>
                <MaterialSymbol name="arrow_back" size={16} />
                {translate('courses.backToCatalogs')}
            </button>
            <h2 className="kv-sub">{translate('courses.coursesIn', { name: catalog?.name })}</h2>

            <div className="cat2">
                <aside className="cat2-side">
                    <div className="fil-group">
                        <h4>Level</h4>
                        {difficultyOptions.map(option => (
                            <label className="fil" key={option.value}>
                                <input
                                    type="checkbox"
                                    checked={levelFilters.includes(option.value)}
                                    onChange={() => setLevelFilters(prev => toggleFilter(prev, option.value))}
                                />
                                <span className="box"><MaterialSymbol name="check" size={12} /></span>
                                {option.label}
                            </label>
                        ))}
                    </div>
                    <div className="fil-group">
                        <h4>Status</h4>
                        {(['aktiv', 'inaktiv'] as StatusFilter[]).map(status => (
                            <label className="fil" key={status}>
                                <input
                                    type="checkbox"
                                    checked={statusFilters.includes(status)}
                                    onChange={() => setStatusFilters(prev => toggleFilter(prev, status))}
                                />
                                <span className="box"><MaterialSymbol name="check" size={12} /></span>
                                {statusLabels[status]}
                            </label>
                        ))}
                    </div>
                    <button type="button" className="fil-reset" onClick={resetFilters}>
                        Filter zurücksetzen
                    </button>
                </aside>

                <section className="cat2-main">
                    <div className="cat2-toolbar">
                        <div className="cat2-search">
                            <MaterialSymbol name="search" size={18} />
                            <input
                                type="text"
                                placeholder="Kurse suchen …"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="cat2-tools">
                            <select
                                className="kv2-sort"
                                aria-label="Sortierung"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as SortOption)}
                            >
                                <option value="name">Name (A–Z)</option>
                                <option value="dur">Dauer</option>
                                <option value="les">Lektionen</option>
                            </select>
                            <div className="viewtoggle">
                                <button
                                    type="button"
                                    className={view === 'grid' ? 'active' : ''}
                                    aria-label="Raster"
                                    onClick={() => setView('grid')}
                                >
                                    <MaterialSymbol name="dashboard" size={18} />
                                </button>
                                <button
                                    type="button"
                                    className={view === 'list' ? 'active' : ''}
                                    aria-label="Liste"
                                    onClick={() => setView('list')}
                                >
                                    <MaterialSymbol name="format_list_bulleted" size={18} />
                                </button>
                            </div>
                            <button type="button" className="btn-out" onClick={() => setSelectFromLibraryOpen(true)}>
                                <MaterialSymbol name="layers" size={16} />
                                {translate('courses.fromLibrary', { _: 'Aus Bibliothek' })}
                            </button>
                            <button type="button" className="btn-solid" onClick={() => handleOpenDialog()}>
                                <MaterialSymbol name="add" size={16} />
                                {translate('courses.newCourse')}
                            </button>
                        </div>
                    </div>

                    <div className="cat2-meta">
                        <span className="kv2-count">
                            {visibleCourses.length === 1 ? '1 Eintrag' : `${visibleCourses.length} Einträge`}
                        </span>
                        <div className="chips">
                            {activeFilterChips.map(value => (
                                <button
                                    type="button"
                                    className="ac"
                                    key={value}
                                    onClick={() => {
                                        setLevelFilters(prev => prev.filter(v => v !== value));
                                        setStatusFilters(prev => prev.filter(v => v !== value));
                                    }}
                                >
                                    {filterChipLabel(value)}
                                    <MaterialSymbol name="close" size={12} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={`kv2-grid${view === 'list' ? ' list' : ''}`}>
                        {visibleCourses.map((course) => (
                            <article className="kvk-card" key={course.id}>
                                <div className="kvk-top">
                                    <h3>{course.title}</h3>
                                    <div className="kvk-tools">
                                        <button
                                            type="button"
                                            className="ic-btn"
                                            aria-label={translate('courseEnrollmentDialog.title')}
                                            title={translate('courseEnrollmentDialog.title')}
                                            onClick={() => { setEnrollmentCourse(course); setEnrollmentDialogOpen(true); }}
                                        >
                                            <MaterialSymbol name="groups" />
                                        </button>
                                        <button
                                            type="button"
                                            className="ic-btn"
                                            aria-label={translate('ra.action.edit')}
                                            title={translate('ra.action.edit')}
                                            onClick={() => handleOpenDialog(course)}
                                        >
                                            <MaterialSymbol name="edit" />
                                        </button>
                                        <button
                                            type="button"
                                            className="ic-btn"
                                            aria-label={translate('ra.action.delete')}
                                            title={translate('ra.action.delete')}
                                            onClick={() => handleDelete(course.id)}
                                        >
                                            <MaterialSymbol name="delete" />
                                        </button>
                                    </div>
                                </div>

                                {course.description && (
                                    <p className="kvk-desc">
                                        {course.description.length > 150
                                            ? `${course.description.substring(0, 150)}...`
                                            : course.description
                                        }
                                    </p>
                                )}

                                <div className="kvk-chips">
                                    <span className="lvl">{getDifficultyLabel(course.difficulty)}</span>
                                    <span className={course.isActive ? 'chip-aktiv' : 'chip-inaktiv'}>
                                        {course.isActive ? translate('catalogs.active') : translate('catalogs.inactive')}
                                    </span>
                                    {course.requireSequentialCompletion && (
                                        <span className="lvl">{translate('courses.sequential')}</span>
                                    )}
                                </div>

                                <div className="kvk-meta">
                                    {!!course.estimatedDuration && (
                                        <span>
                                            <MaterialSymbol name="schedule" size={14} />
                                            {translate('courses.duration', { minutes: course.estimatedDuration })}
                                        </span>
                                    )}
                                    <span>
                                        <MaterialSymbol name="layers" size={14} />
                                        {translate('courses.lessonsCount', { count: course.lessons?.length || 0 })}
                                    </span>
                                </div>

                                {courseRatings[course.id] && (
                                    <div className="kvk-rating">
                                        <RatingDisplay
                                            averageRating={courseRatings[course.id].averageRating}
                                            ratingCount={courseRatings[course.id].ratingCount}
                                            size="small"
                                        />
                                    </div>
                                )}

                                {course.qualityScore != null && (
                                    <button
                                        type="button"
                                        className={qualityChipClass(course.qualityScore)}
                                        onClick={() => { setQualityCourse(course); setQualityDialogOpen(true); }}
                                    >
                                        <MaterialSymbol name="lightbulb" size={13} />
                                        {`KI-Qualität: ${course.qualityScore}/100`}
                                    </button>
                                )}

                                <div className="kvk-foot">
                                    <button
                                        type="button"
                                        className="kvk-lessons"
                                        onClick={() => navigate(`/courses/${course.id}/lessons`)}
                                    >
                                        {translate('courses.manageLessons')}
                                    </button>
                                    <div className="kvk-foot-ic">
                                        <button
                                            type="button"
                                            className="ic-btn"
                                            aria-label="KI-Qualitätsprüfung"
                                            title="KI-Qualitätsprüfung"
                                            onClick={() => { setQualityCourse(course); setQualityDialogOpen(true); }}
                                        >
                                            <MaterialSymbol name="lightbulb" />
                                        </button>
                                        <button
                                            type="button"
                                            className="ic-btn"
                                            aria-label={translate('library.saveDialog.title')}
                                            title={translate('library.saveDialog.title')}
                                            onClick={() => handleSaveToLibrary(course)}
                                        >
                                            <MaterialSymbol name="note_add" />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>

                    {courses.length === 0 ? (
                        <div className="kv-create-empty">
                            <p>{translate('courses.noCourses')}</p>
                            <button type="button" className="btn-solid" onClick={() => handleOpenDialog()}>
                                <MaterialSymbol name="add" size={16} />
                                {translate('courses.createFirst')}
                            </button>
                        </div>
                    ) : visibleCourses.length === 0 && (
                        <p className="cat-empty">Keine Kurse entsprechen den Filtern.</p>
                    )}
                </section>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingCourse ? translate('courses.editCourse') : translate('courses.newCourse')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label={translate('courses.form.title')}
                            fullWidth
                            required
                            value={formData.title}
                            onChange={(e) => {
                                const title = e.target.value;
                                setFormData({
                                    ...formData,
                                    title,
                                    slug: !editingCourse ? generateSlug(title) : formData.slug
                                });
                            }}
                        />

                        <TextField
                            label={translate('courses.form.slug')}
                            fullWidth
                            required
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            helperText={translate('courses.form.slugHelp')}
                        />

                        <TextField
                            label={translate('courses.form.description')}
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                                label={translate('courses.form.estimatedDuration')}
                                type="number"
                                fullWidth
                                value={formData.estimatedDuration}
                                onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 0 })}
                            />

                            <FormControl fullWidth>
                                <InputLabel>{translate('courses.form.difficulty')}</InputLabel>
                                <Select
                                    value={formData.difficulty}
                                    label={translate('courses.form.difficulty')}
                                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                                >
                                    {difficultyOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <TextField
                            label={translate('courses.form.tags', { _: 'Tags' })}
                            fullWidth
                            value={formData.tags}
                            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                            helperText={translate('courses.form.tagsHelp', { _: 'Komma-getrennte Liste (z.B. react, javascript, frontend)' })}
                            placeholder="react, javascript, frontend"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.requireSequentialCompletion}
                                    onChange={(e) => setFormData({ ...formData, requireSequentialCompletion: e.target.checked })}
                                />
                            }
                            label={translate('courses.form.requireSequentialCompletion')}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            }
                            label={translate('courses.form.active')}
                        />

                        <FormControl fullWidth required error={certificateTypeMissing}>
                            <InputLabel id="certificate-type-label">{translate('courses.form.certificateType')}</InputLabel>
                            <Select
                                labelId="certificate-type-label"
                                value={formData.certificateType}
                                label={translate('courses.form.certificateType')}
                                onChange={(e) => setFormData({ ...formData, certificateType: e.target.value as CertificateType })}
                            >
                                <MenuItem value="participated">{translate('courses.form.certificateTypeParticipated')}</MenuItem>
                                <MenuItem value="passed">{translate('courses.form.certificateTypePassed')}</MenuItem>
                                <MenuItem value="none">{translate('courses.form.certificateTypeNone')}</MenuItem>
                            </Select>
                            {certificateTypeMissing && <FormHelperText>{translate('courses.form.certificateTypeRequired')}</FormHelperText>}
                        </FormControl>
                        {showThresholdWarning && (
                            <Alert severity="warning">{translate('courses.form.certificateTypePassedWithoutThreshold')}</Alert>
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.aiTutorEnabled}
                                    onChange={(e) => setFormData({ ...formData, aiTutorEnabled: e.target.checked })}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TutorIcon fontSize="small" />
                                    {translate('courses.form.aiTutorEnabled', { _: 'KI-Lernassistent aktivieren' })}
                                </Box>
                            }
                        />

                        {/* Issue #197 (EU-KI-Act Art. 14): Pflicht-Review der KI-Bewertungen */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.requireTeacherReviewForGrading ?? false}
                                    onChange={(e) => setFormData({ ...formData, requireTeacherReviewForGrading: e.target.checked })}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TutorIcon fontSize="small" />
                                    {translate('courses.form.requireTeacherReviewForGrading', { _: 'KI-Bewertungen erfordern Freigabe durch eine berechtigte Person (zertifikats-relevante Kurse)' })}
                                </Box>
                            }
                        />

                        <FormControl fullWidth>
                            <InputLabel>Einschreibungsmodus</InputLabel>
                            <Select
                                value={formData.enrollmentMode}
                                label="Einschreibungsmodus"
                                onChange={(e) => setFormData({ ...formData, enrollmentMode: e.target.value })}
                            >
                                <MenuItem value="none">Keine Selbsteinschreibung</MenuItem>
                                <MenuItem value="self">Selbsteinschreibung erlaubt</MenuItem>
                                <MenuItem value="request">Einschreibung anfragen</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            type="number"
                            label="Wiederholungsrhythmus (Wochen)"
                            value={formData.recurrenceWeeks}
                            onChange={(e) => setFormData({ ...formData, recurrenceWeeks: e.target.value })}
                            helperText="Leer = keine Wiederholung. Nach Ablauf muss der Kurs erneut absolviert werden. Zertifikat verfällt automatisch."
                            inputProps={{ min: 1 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{translate('ra.action.cancel')}</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!formData.title.trim() || !formData.slug.trim()}>
                        {translate('ra.action.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Save to Library Dialog */}
            {courseToSave && (
                <SaveToLibraryDialog
                    open={saveLibraryDialogOpen}
                    onClose={() => {
                        setSaveLibraryDialogOpen(false);
                        setCourseToSave(null);
                    }}
                    contentType="course"
                    contentId={courseToSave.id}
                    contentTitle={courseToSave.title}
                    contentDescription={courseToSave.description}
                    contentTags={courseToSave.tags}
                />
            )}

            {/* Select from Library Dialog */}
            <SelectFromLibraryDialog
                open={selectFromLibraryOpen}
                onClose={() => setSelectFromLibraryOpen(false)}
                itemType="course"
                targetId={parseInt(catalogId!)}
                onInserted={loadCourses}
            />

            {/* Course Quality Dialog */}
            {qualityCourse && (
                <CourseQualityDialog
                    open={qualityDialogOpen}
                    onClose={() => { setQualityDialogOpen(false); setQualityCourse(null); }}
                    courseId={qualityCourse.id}
                    courseTitle={qualityCourse.title}
                    existingData={qualityCourse.qualityCheckData
                        ? { ...qualityCourse.qualityCheckData, qualityCheckedAt: qualityCourse.qualityCheckedAt }
                        : null}
                    onQualityUpdated={(data) => {
                        setCourses(prev => prev.map(c =>
                            c.id === qualityCourse.id
                                ? { ...c, qualityScore: data.overallScore, qualityCheckData: data, qualityCheckedAt: data.qualityCheckedAt }
                                : c
                        ));
                    }}
                />
            )}

            {enrollmentCourse && (
                <CourseEnrollmentDialog
                    open={enrollmentDialogOpen}
                    onClose={() => { setEnrollmentDialogOpen(false); setEnrollmentCourse(null); }}
                    courseId={enrollmentCourse.id}
                    courseTitle={enrollmentCourse.title}
                />
            )}
        </div>
    );
};

export default CatalogCourses;
